import { FunctionComponent, useCallback, useMemo } from 'react'

import styled, { css } from 'styled-components'

import translate from '@i18n'
import useFollowMyTeam from '@hooks/useFollowMyTeam'
import useSportEntities from '@hooks/useSportEntities'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import PrimaryCTAButton from '@components/Buttons/PrimaryCTAButton'
import SecondaryCTAButton from '@components/Buttons/SecondaryCTAButton'

type TextboxFmTButtonProps = {
  entityId: string
}

const StyledPrimaryCTAButton = styled(PrimaryCTAButton)`
  ${() => css`
    display: flex;
    align-self: center;
  `}
`

const StyledSecondaryCTAButton = styled(SecondaryCTAButton)`
  ${() => css`
    display: flex;
    align-self: center;
  `}
`

const TextboxFmTButton: FunctionComponent<TextboxFmTButtonProps> = ({
  entityId,
}) => {
  const { getSportEntityById } = useSportEntities()
  const sportEntity = useMemo(
    () => getSportEntityById(entityId),
    [entityId, getSportEntityById],
  )

  const useFollowMyTeamProps = useMemo(
    () => ({
      entityType: sportEntity?.entityType ?? 'default',
      enabled: true,
    }),
    [sportEntity],
  )

  const { isFollowing, toggleFollow } = useFollowMyTeam(useFollowMyTeamProps)

  const onAddTeamClickEvent = useCallback<TrackingFnType<{ entityId: string }>>(
    ({ extraData }) => ({
      event: 'add_team',
      entity_id: extraData.entityId,
    }),
    [],
  )

  const trackedOnAddTeamClick = useTracking(onAddTeamClickEvent)

  const onToggleFollowing = useCallback(() => {
    if (sportEntity) {
      toggleFollow(sportEntity)
      if (!isFollowing(sportEntity.entityId)) {
        trackedOnAddTeamClick({ entityId: sportEntity.entityId })
      }
    }
  }, [sportEntity, toggleFollow, trackedOnAddTeamClick, isFollowing])
  return isFollowing(entityId) ? (
    <StyledSecondaryCTAButton
      colorVariant="black"
      size="small"
      iconName="check-circle-solid"
      onClick={onToggleFollowing}
    >
      {translate('entityHeader.following')}
    </StyledSecondaryCTAButton>
  ) : (
    <StyledPrimaryCTAButton
      colorVariant="black"
      size="small"
      iconName="plus-circle"
      onClick={onToggleFollowing}
    >
      {translate('entityTextbox.followButtonText', {
        teamName: sportEntity?.title ?? '',
      })}
    </StyledPrimaryCTAButton>
  )
}

export default TextboxFmTButton
